import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, FileText, AlertTriangle, Eye, Network, Lightbulb, Zap, Check } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
const reveal = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
interface SectionProps {
  num: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  id?: string;
}
function Section({
  num,
  title,
  subtitle,
  children,
  id
}: SectionProps) {
  return <motion.section id={id} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-80px'
  }} variants={reveal} className="py-16 border-t border-slate-200 first:border-t-0" data-id="element-59">
      <div className="mb-10" data-id="element-60">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue mb-3" data-id="element-61">
          {num}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-navy" data-id="element-62">
          {title}
        </h2>
        {subtitle && <p className="mt-3 text-lg text-slate-600 max-w-2xl leading-relaxed" data-id="element-63">
            {subtitle}
          </p>}
      </div>
      {children}
    </motion.section>;
}
interface SwatchProps {
  name: string;
  hex: string;
  usage: string;
  className: string;
  dark?: boolean;
}
function Swatch({
  name,
  hex,
  usage,
  className,
  dark
}: SwatchProps) {
  return <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-id="element-64">
      <div className={`h-24 ${className} ${dark ? 'border-b border-slate-700' : ''}`} data-id="element-65" />
      <div className="p-4" data-id="element-66">
        <div className="font-semibold text-navy" data-id="element-67">{name}</div>
        <div className="text-xs font-mono text-slate-500 mt-0.5 uppercase" data-id="element-68">
          {hex}
        </div>
        <div className="text-sm text-slate-600 mt-2" data-id="element-69">{usage}</div>
      </div>
    </div>;
}
const COLORS: SwatchProps[] = [{
  name: 'Primario',
  hex: '#0F172A',
  usage: 'Fondo oscuro, titulares, consolas de datos.',
  className: 'bg-navy',
  dark: true
}, {
  name: 'Secundario',
  hex: '#2563EB',
  usage: 'Acciones primarias, enlaces, énfasis.',
  className: 'bg-techblue'
}, {
  name: 'Acento',
  hex: '#38BDF8',
  usage: 'Métricas, destellos sobre fondo oscuro.',
  className: 'bg-cyan'
}, {
  name: 'Riesgo',
  hex: '#DC2626',
  usage: 'Solo para alertas y hallazgos críticos.',
  className: 'bg-risk'
}, {
  name: 'Fondo',
  hex: '#F8FAFC',
  usage: 'Lienzo claro de las secciones narrativas.',
  className: 'bg-light'
}, {
  name: 'Superficie',
  hex: '#FFFFFF',
  usage: 'Tarjetas y paneles sobre fondo claro.',
  className: 'bg-white border-b border-slate-200'
}, {
  name: 'Texto / borde',
  hex: '#64748B',
  usage: 'Texto secundario (slate-500) y bordes sutiles.',
  className: 'bg-slate-500',
  dark: true
}, {
  name: 'Neutro oscuro',
  hex: '#1E293B',
  usage: 'Paneles y bordes dentro de consolas (slate-800).',
  className: 'bg-slate-800',
  dark: true
}];
const TYPE_SCALE = [{
  role: 'H1 — Titular',
  classes: 'text-4xl md:text-6xl font-bold tracking-tight',
  sample: 'Entiende mejor.'
}, {
  role: 'H2 — Sección',
  classes: 'text-3xl md:text-4xl font-bold tracking-tight',
  sample: 'Inteligencia accionable'
}, {
  role: 'H3 — Subtítulo',
  classes: 'text-xl font-semibold',
  sample: 'Detecta contradicciones'
}, {
  role: 'Cuerpo',
  classes: 'text-lg text-slate-600',
  sample: 'Prisma analiza cientos de documentos para revelar riesgos ocultos.'
}, {
  role: 'Etiqueta',
  classes: 'text-xs font-medium uppercase tracking-wider text-slate-500',
  sample: 'Riesgos detectados'
}];
const VALUES = ['claridad', 'entendimiento', 'ventaja', 'descubrimiento', 'visión', 'inteligencia'];
const ICONS = [ShieldAlert, FileText, AlertTriangle, Eye, Network, Lightbulb, Zap];
export function StyleGuide() {
  return <div className="min-h-screen w-full bg-light font-sans text-navy" data-id="element-70">
      {/* Top bar */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-md" data-id="element-71">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between" data-id="element-72">
          <Logo data-id="element-73" />
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-navy transition-colors focus:outline-none focus:ring-2 focus:ring-techblue rounded-sm px-1" data-id="element-74">
            <ArrowLeft size={16} data-id="element-75" />
            Volver al sitio
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 max-w-5xl pb-24" data-id="element-76">
        {/* Cover */}
        <section className="py-16" data-id="element-77">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue mb-4" data-id="element-78">
            Design Book
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-navy leading-tight mb-6" data-id="element-79">
            Sistema de diseño PRISMA
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed mb-8" data-id="element-80">
            Un prisma recibe luz compleja y la transforma en claridad. PRISMA
            convierte miles de documentos complejos en inteligencia accionable.
            Este es el lenguaje visual que sostiene esa promesa.
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-81">
            {VALUES.map(v => <span key={v} className="px-3 py-1 bg-white text-slate-700 text-sm font-medium rounded-full border border-slate-200 capitalize" data-id="element-82">
                {v}
              </span>)}
          </div>
        </section>

        {/* 01 Logo */}
        <Section num="01" title="Logo" subtitle="Un triángulo prisma con líneas internas de refracción, acompañado del logotipo PRISMA en Inter bold con tracking ajustado." data-id="element-83">
          <div className="grid sm:grid-cols-2 gap-6 mb-8" data-id="element-84">
            <div className="rounded-xl border border-slate-200 bg-white p-12 flex items-center justify-center" data-id="element-85">
              <Logo data-id="element-86" />
            </div>
            <div className="rounded-xl border border-slate-800 bg-navy p-12 flex items-center justify-center" data-id="element-87">
              <Logo dark data-id="element-88" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-89">
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-90">
              <div className="flex items-center gap-2 text-emerald-600 font-semibold mb-3" data-id="element-91">
                <Check size={18} data-id="element-92" /> Correcto
              </div>
              <ul className="text-sm text-slate-600 space-y-1.5" data-id="element-93">
                <li data-id="element-94">
                  Respeta un espacio libre mínimo igual a la altura del
                  triángulo.
                </li>
                <li data-id="element-95">
                  Azul (#2563EB) sobre claro; celeste (#38BDF8) sobre oscuro.
                </li>
                <li data-id="element-96">Tamaño mínimo legible: ~24px de alto.</li>
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-97">
              <div className="flex items-center gap-2 text-risk font-semibold mb-3" data-id="element-98">
                Evitar
              </div>
              <ul className="text-sm text-slate-600 space-y-1.5" data-id="element-99">
                <li data-id="element-100">No deformar ni estirar el triángulo.</li>
                <li data-id="element-101">No recolorear fuera de la paleta.</li>
                <li data-id="element-102">No agregar sombras ni degradados al símbolo.</li>
              </ul>
            </div>
          </div>
        </Section>

        {/* 02 Color */}
        <Section num="02" title="Color" subtitle="Una base sobria de azul profundo y blanco, con acentos tecnológicos. El rojo se reserva exclusivamente para alertas." data-id="element-103">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-104">
            {COLORS.map(c => <Swatch key={c.name} {...c} data-id="element-105" />)}
          </div>
        </Section>

        {/* 03 Tipografía */}
        <Section num="03" title="Tipografía" subtitle="Inter en toda la interfaz. Titulares densos y confiados con tracking ajustado; cuerpo legible en slate." data-id="element-106">
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-id="element-107">
            {TYPE_SCALE.map(t => <div key={t.role} className="p-6 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-8" data-id="element-108">
                <div className="w-40 shrink-0" data-id="element-109">
                  <div className="text-sm font-semibold text-navy" data-id="element-110">
                    {t.role}
                  </div>
                  <div className="text-xs font-mono text-slate-400 mt-1" data-id="element-111">
                    {t.classes}
                  </div>
                </div>
                <div className={`${t.classes} text-navy`} data-id="element-112">{t.sample}</div>
              </div>)}
          </div>
        </Section>

        {/* 04 Botones */}
        <Section num="04" title="Botones y acciones" subtitle="Solid para la acción principal, outline sobre fondos oscuros, ghost para acciones secundarias." data-id="element-113">
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-114">
            <div className="rounded-xl border border-slate-200 bg-white p-8 flex flex-wrap items-center gap-4" data-id="element-115">
              <Button variant="solid" data-id="element-116">Solicitar Demo</Button>
              <Button variant="ghost" data-id="element-117">Ver Ejemplo</Button>
              <Button variant="solid" size="sm" data-id="element-118">
                Pequeño
              </Button>
              <Button variant="solid" size="lg" data-id="element-119">
                Grande
              </Button>
            </div>
            <div className="rounded-xl border border-slate-800 bg-navy p-8 flex flex-wrap items-center gap-4" data-id="element-120">
              <Button variant="solid" data-id="element-121">Solicitar Demo</Button>
              <Button variant="outline" data-id="element-122">Ver Ejemplo</Button>
            </div>
          </div>
        </Section>

        {/* 05 Patrones */}
        <Section num="05" title="Patrones de diseño" subtitle="Secciones narrativas claras que respiran, alternadas con consolas de datos oscuras para los momentos de prueba." data-id="element-123">
          <div className="grid md:grid-cols-2 gap-6" data-id="element-124">
            {/* Light card pattern */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-125">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-126">
                Tarjeta clara
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-id="element-127">
                <Network className="w-8 h-8 text-techblue mb-3" data-id="element-128" />
                <h3 className="text-lg font-semibold" data-id="element-129">
                  Relaciona información.
                </h3>
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-130">
                bg-white · border-slate-200 · rounded-xl · hover lift
              </p>
            </div>

            {/* Dark metric pattern */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-131">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-132">
                Métrica en consola
              </div>
              <div className="rounded-xl border border-slate-800 bg-navy p-6 text-center" data-id="element-133">
                <div className="text-3xl font-bold text-cyan mb-1" data-id="element-134">2.5M</div>
                <div className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-135">
                  Documentos analizados
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-136">
                bg-slate-900 · border-slate-800 · número en acento
              </p>
            </div>

            {/* Alert / finding row */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-137">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-138">
                Hallazgo crítico
              </div>
              <div className="flex items-start gap-3 p-3 bg-risk/10 border border-risk/20 rounded-md" data-id="element-139">
                <div className="w-2 h-2 rounded-full bg-risk mt-1.5 shrink-0" data-id="element-140" />
                <div data-id="element-141">
                  <p className="text-sm font-medium text-navy" data-id="element-142">
                    Multa excede límite legal
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-143">
                    Bases Administrativas · Pág 42
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-144">
                Color riesgo solo para alertas.
              </p>
            </div>

            {/* Grid/glow background */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-145">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-146">
                Fondo de consola
              </div>
              <div className="relative h-28 rounded-xl border border-slate-800 bg-navy overflow-hidden" data-id="element-147">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-30" data-id="element-148" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-20 bg-techblue/20 blur-2xl rounded-full" data-id="element-149" />
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-150">
                Grilla sutil + resplandor azul difuso.
              </p>
            </div>
          </div>
        </Section>

        {/* 06 Iconografía y motion */}
        <Section num="06" title="Iconografía y movimiento" subtitle="Íconos lineales de lucide-react y animaciones snappy, siempre con propósito." data-id="element-151">
          <div className="rounded-xl border border-slate-200 bg-white p-8 mb-6" data-id="element-152">
            <div className="flex flex-wrap gap-6" data-id="element-153">
              {ICONS.map((Icon, i) => <Icon key={i} className="w-7 h-7 text-techblue" data-id="element-154" />)}
            </div>
            <p className="text-sm text-slate-500 mt-5" data-id="element-155">
              lucide-react · trazo lineal · importación con sufijo Icon · color
              techblue o slate-400.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-8" data-id="element-156">
            <ul className="text-sm text-slate-600 space-y-2" data-id="element-157">
              <li data-id="element-158">
                <span className="font-semibold text-navy" data-id="element-159">Entrada:</span> fade +
                desplazamiento sutil (y: 16 → 0), duración ~0.5s.
              </li>
              <li data-id="element-160">
                <span className="font-semibold text-navy" data-id="element-161">Scroll:</span>{' '}
                whileInView con once: true — revelar una sola vez.
              </li>
              <li data-id="element-162">
                <span className="font-semibold text-navy" data-id="element-163">Stagger:</span>{' '}
                retardo escalonado de 0.05–0.1s entre elementos.
              </li>
              <li data-id="element-164">
                <span className="font-semibold text-navy" data-id="element-165">Hover:</span> solo en
                elementos interactivos (botones, enlaces, tarjetas).
              </li>
            </ul>
          </div>
        </Section>
      </main>

      <footer className="bg-navy text-slate-400 py-10 border-t border-slate-800" data-id="element-166">
        <div className="container mx-auto px-4 max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-167">
          <Logo dark data-id="element-168" />
          <p className="text-sm" data-id="element-169">© 2026 PRISMA · Sistema de diseño</p>
        </div>
      </footer>
    </div>;
}